import type { AtlasLensTheme } from './atlasLensStyles';
import {
  ATLAS_LENS_COLORS,
  ATLAS_LENS_COMPONENT_VARS,
  ATLAS_LENS_SEMANTIC_VARS,
  resolveAtlasTokenString,
} from './atlasLensTokens';

export { ATLAS_LENS_COLORS, ATLAS_LENS_SEMANTIC_VARS, ATLAS_LENS_COMPONENT_VARS, resolveAtlasTokenString } from './atlasLensTokens';
export {
  atlasButtonLgSx,
  atlasFontLabelSmEmphasis,
  atlasFontLabelXs,
  atlasFontStepBadge,
  atlasFontStepLabel,
  atlasFontTextBody,
  atlasFontTextBodyEmphasis,
  atlasFontTextMd,
  atlasFontTextMdEmphasis,
  atlasFontTextSm,
  atlasFontTitleH1Billboard,
  atlasFontTitleH3Lg,
  atlasFontTitleH4Md,
  atlasFontTitleH5Sm,
  atlasFontTitleH5SmEmphasis,
  atlasFontTitleH7,
  atlasGhostIconButtonSx,
  atlasInverseGhostHoverSx,
  atlasLensTooltipSlotProps,
  atlasModalShadow,
  atlasRadiusLg,
  atlasRadiusLg16,
  atlasSpacing,
  type AtlasLensTheme,
} from './atlasLensStyles';

/** Fallback hex when Atlas theme tokens omit optional semantic paths (e.g. outline). */
const LENS_COLOR_FALLBACK = {
  typeDefault: '#282e37',
  typeMuted: '#6f7377',
  typeDisabled: '#a0a2a5',
  outlineDefault: '#6f7377',
  outlineActive: '#000000',
  surface: '#ffffff',
  divider: '#dadada',
  focusRing: '#0b4cce',
  focusInverse: '#ffffff',
  accentRedHighlight: '#d3222a',
  accentRedRequired: '#ee312e',
  statusErrorDefault: '#921a1d',
  statusErrorText: '#d3222a',
  statusSuccessBackground: '#d2ff9c',
  statusSuccessContent: '#3f6900',
  actionPrimaryDefault: '#121a26',
  actionPrimaryOnPrimary: '#ffffff',
  surfaceSubtle: '#e6e6e6',
  surfaceVariant: '#f3f3f3',
  toastSurface: '#282e37',
  modalShadowHigh:
    '0px 0px 2px rgba(22, 27, 43, 0.1), 0px 10px 24px rgba(22, 27, 43, 0.16)',
  statusWarningContent: '#6a5f00',
  cardSelected: '#d7f6ff',
  cardSelectedHover: '#a8edff',
  coreBlue80: '#1dd9f9',
} as const;

type TokenStringValue =
  | string
  | {
      stringValue?: string;
      layers?: unknown[];
    };

type ThemeWithTokens = {
  tokens?: {
    core?: {
      color?: {
        brand?: {
          primary?: {
            diligentRed?: { value: string };
          };
        };
      };
    };
    semantic?: {
      color?: {
        type?: { default?: { value: string }; muted?: { value: string }; inverse?: { value: string } };
        background?: { base?: { value: string }; container?: { value: string } };
        accent?: { red?: { content?: { value: string } }; blue?: { background?: { value: string } } };
        status?: {
          error?: {
            text?: { value: string };
            default?: { value: string };
            hover?: { value: string };
          };
          warning?: {
            content?: { value: string };
            text?: { value: string };
            default?: { value: string };
          };
          success?: {
            default?: { value: string };
            background?: { value: string };
            content?: { value: string };
          };
        };
        action?: {
          primary?: {
            default?: { value: string };
            onPrimary?: { value: string };
            hover?: { value: string };
          };
          ghost?: {
            hover?: { value: string };
          };
        };
        surface?: {
          subtle?: { value: string };
          default?: { value: string };
          variant?: { value: string; subtle?: { value: string } };
        };
        ui?: {
          divider?: { default?: { value: string } };
          focusRing?: { value: string };
          focusInverse?: { value: string };
        };
        outline?: { default?: { value: string }; active?: { value: string } };
        selection?: { secondary?: { hover?: { value: string } } };
      };
    };
    component?: {
      modal?: { boxShadow?: { value: TokenStringValue } };
      input?: { medium?: { container?: { height?: { value: string } } } };
    };
  };
};

/** Resolve Lens semantic colors from MUI theme with safe hex fallbacks for runtime. */
export function resolveAtlasLensColors(theme: ThemeWithTokens | AtlasLensTheme) {
  type SemanticColor = NonNullable<
    NonNullable<ThemeWithTokens['tokens']>['semantic']
  >['color'];
  const c = theme.tokens?.semantic?.color as SemanticColor | undefined;
  const ui = c?.ui;
  const surface = c?.background?.base?.value ?? LENS_COLOR_FALLBACK.surface;

  return {
    typeDefault: c?.type?.default?.value ?? LENS_COLOR_FALLBACK.typeDefault,
    typeMuted: c?.type?.muted?.value ?? LENS_COLOR_FALLBACK.typeMuted,
    typeDisabled:
      (c?.type as { disabled?: { value: string } } | undefined)?.disabled?.value ??
      LENS_COLOR_FALLBACK.typeDisabled,
    typeInverse: c?.type?.inverse?.value ?? LENS_COLOR_FALLBACK.actionPrimaryOnPrimary,
    outlineDefault: c?.outline?.default?.value ?? LENS_COLOR_FALLBACK.outlineDefault,
    outlineActive: c?.outline?.active?.value ?? LENS_COLOR_FALLBACK.outlineActive,
    surface,
    divider: ui?.divider?.default?.value ?? LENS_COLOR_FALLBACK.divider,
    accentRedHighlight: c?.accent?.red?.content?.value ?? LENS_COLOR_FALLBACK.accentRedHighlight,
    /** Figma label suffix — Brand/Primary/Diligent Red (#EE312E). */
    accentRedRequired:
      theme.tokens?.core?.color?.brand?.primary?.diligentRed?.value ??
      LENS_COLOR_FALLBACK.accentRedRequired,
    statusErrorDefault:
      c?.status?.error?.default?.value ?? LENS_COLOR_FALLBACK.statusErrorDefault,
    statusErrorText: c?.status?.error?.text?.value ?? LENS_COLOR_FALLBACK.statusErrorText,
    statusSuccessBackground:
      c?.status?.success?.background?.value ?? LENS_COLOR_FALLBACK.statusSuccessBackground,
    statusSuccessContent:
      c?.status?.success?.content?.value ??
      c?.status?.success?.default?.value ??
      LENS_COLOR_FALLBACK.statusSuccessContent,
    actionPrimaryDefault:
      c?.action?.primary?.default?.value ?? LENS_COLOR_FALLBACK.actionPrimaryDefault,
    actionPrimaryOnPrimary:
      c?.action?.primary?.onPrimary?.value ?? LENS_COLOR_FALLBACK.actionPrimaryOnPrimary,
    surfaceSubtle: c?.surface?.subtle?.value ?? LENS_COLOR_FALLBACK.surfaceSubtle,
    surfaceVariant: c?.surface?.variant?.value ?? LENS_COLOR_FALLBACK.surfaceVariant,
    surfaceVariantSubtle: c?.surface?.variant?.subtle?.value ?? '#fafafa',
    toastSurface: c?.type?.default?.value ?? LENS_COLOR_FALLBACK.toastSurface,
    modalShadowHigh: resolveAtlasTokenString(
      theme.tokens?.component?.modal?.boxShadow?.value,
      LENS_COLOR_FALLBACK.modalShadowHigh,
    ),
    statusWarningContent:
      c?.status?.warning?.content?.value ??
      c?.status?.warning?.text?.value ??
      LENS_COLOR_FALLBACK.statusWarningContent,
    cardBorder: ui?.divider?.default?.value ?? LENS_COLOR_FALLBACK.divider,
    cardBackground: surface,
    cardBackgroundHover: c?.surface?.variant?.value ?? LENS_COLOR_FALLBACK.surfaceVariant,
    cardSelectedBackground: c?.accent?.blue?.background?.value ?? LENS_COLOR_FALLBACK.cardSelected,
    cardSelectedHover:
      c?.selection?.secondary?.hover?.value ?? LENS_COLOR_FALLBACK.cardSelectedHover,
    coreBlue80:
      (theme.tokens?.core?.color as { blue?: { '80'?: { value: string } } } | undefined)?.blue?.[
        '80'
      ]?.value ?? LENS_COLOR_FALLBACK.coreBlue80,
    focusRing: `0 0 0 1px ${ui?.focusInverse?.value ?? LENS_COLOR_FALLBACK.focusInverse}, 0 0 0 3px ${ui?.focusRing?.value ?? LENS_COLOR_FALLBACK.focusRing}`,
  };
}

/**
 * Form field layout and typography — aligned with CEE-499 / onboarding Figma (node 16692:5503).
 * Uses Atlas design tokens for colors to maintain consistency with Diligent design system.
 */
export const FORM_FIELD_SPEC = {
  formField: {
    labelGap: 4,
    labelToInputGap: 8,
    inputRadius: 8,
  },
  dropdownRow: {
    gap: 24,
  },
  sectionGap: 24,
  optionCard: {
    /** Figma 16939:3708 — List item clickable */
    listGap: 16,
    paddingX: 12,
    paddingY: 12,
    minHeight: 68,
    borderRadius: 8,
    radioGap: 16,
    labelGap: 4,
    radioSize: 24,
  },
  checkboxGrid: {
    columnGap: 24,
    checkboxGap: 12,
    rowPaddingY: 12,
    checkboxSize: 24,
    checkboxRadius: 4,
    /** Compact scroll area for dense forms (e.g. AI Recommendation demo). */
    listMaxHeight: 240,
  },
  typography: {
    labelEmphasis: {
      fontSize: 12,
      lineHeight: 16,
      fontWeight: 600,
      letterSpacing: 0.3,
    },
    labelRequired: {
      fontSize: 12,
      lineHeight: 16,
      fontWeight: 400,
      letterSpacing: 0.3,
    },
    body: {
      fontSize: 16,
      lineHeight: 24,
      fontWeight: 400,
      letterSpacing: 0.2,
    },
    bodyEmphasis: {
      fontSize: 16,
      lineHeight: 24,
      fontWeight: 600,
      letterSpacing: 0.2,
    },
    helper: {
      fontSize: 14,
      lineHeight: 20,
      fontWeight: 400,
      letterSpacing: 0.2,
    },
    /** Semantic/Font/Text/Sm — radio-card context text (Figma 16939:3708). */
    textSm: {
      fontSize: 12,
      lineHeight: 16,
      fontWeight: 400,
      letterSpacing: 0.3,
    },
  },
  colors: ATLAS_LENS_COLORS,
} as const;

/** Figma Input shell — box model; row flex matches MUI OutlinedInput (adornment + input). */
export const onboardingOutlinedInputShellSx = {
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  isolation: 'isolate',
  backgroundColor: ATLAS_LENS_SEMANTIC_VARS.surfaceDefault,
} as const;

/**
 * Figma Input (focused) — semantic.outline.active border + semantic.shadow.focus ring.
 * Nest under TextField `& .MuiOutlinedInput-root`, or pass directly to OutlinedInput (e.g. Select input).
 */
export const onboardingOutlinedInputFocusSx = {
  '& fieldset, & .MuiOutlinedInput-notchedOutline': {
    borderColor: ATLAS_LENS_COMPONENT_VARS.inputOutlinedBorder,
    borderWidth: '1px',
  },
  '&:hover fieldset, &:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: ATLAS_LENS_COMPONENT_VARS.inputOutlinedBorderHover,
  },
  '&&.Mui-focused fieldset, &&.Mui-focused .MuiOutlinedInput-notchedOutline, &&.Mui-error.Mui-focused fieldset, &&.Mui-error.Mui-focused .MuiOutlinedInput-notchedOutline':
    {
      borderColor: ATLAS_LENS_COMPONENT_VARS.inputOutlinedBorderActive,
      borderWidth: '1px',
    },
  '&&.Mui-focused': {
    boxShadow: ATLAS_LENS_SEMANTIC_VARS.shadowFocus,
  },
} as const;

/** Figma Input — shell + focus styles for OutlinedInput root. */
export const onboardingOutlinedInputRootSx = {
  ...onboardingOutlinedInputShellSx,
  ...onboardingOutlinedInputFocusSx,
} as const;
